import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import get from 'lodash/get';
import camelcase from 'lodash/camelCase';

import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
  trackEventGa,
} from '../../../context/host/consumer';
import Tab from '../../../components/Tab';
import Organizations from './Organizations';
import Athletics from './Athletics';
import GreekLife from './GreekLife';
import ROTC from './ROTC';
import Services from './Services';
import ComputingResources from './ComputingResources';
import './styles.css';

const ROTC_SERVICES = [
  'ROTCArmy',
  'ROTCNavy',
  'ROTCAirforce',
  'ROTCServicepersonOpportunityCollege',
];

const messages = defineMessages({
  title: {
    id: 'hubs.organizations-and-services.title',
    defaultMessage: 'Student Organizations and Services',
  },
  organizations: {
    id: 'hubs.organizations-and-services.organizations',
    defaultMessage: 'Organizations',
  },
  athletics: {
    id: 'hubs.organizations-and-services.athletics',
    defaultMessage: 'Athletics',
  },
  greekLife: {
    id: 'hubs.organizations-and-services.greek-life',
    defaultMessage: 'Greek Life',
  },
  rotc: {
    id: 'hubs.organizations-and-services.rotc',
    defaultMessage: 'ROTC',
  },
  services: {
    id: 'hubs.organizations-and-services.services',
    defaultMessage: 'Services',
  },
  computingResources: {
    id: 'hubs.organizations-and-services.computing-resources',
    defaultMessage: 'Computing Resources',
  },
});

function hasData(dataObject) {
  return Object.keys(dataObject).some((key) => get(dataObject, `${key}.value`, false));
}

function getMenuItem(intl, selected, index, message, setActiveTab) {
  const tabName = intl.formatMessage(message);
  return {
    content: tabName,
    role: 'tab',
    'aria-label': tabName,
    'aria-selected': selected,
    tabIndex: 0,
    onKeyPress: (e) => {
      /* istanbul ignore else */
      if (e.key === 'Enter') {
        setActiveTab(index);
        setTimeout(() => {
          document.activeElement.classList.add('focus-visible');
        });
      }
    },
    'data-index': index,
    key: message.defaultMessage,
  };
}

function getPanes(props, activeTab, setActiveTab) {
  const panes = [];
  const {
    intl,
    organizations,
    athletics,
    greekLife,
    services,
    computingResources,
  } = props;
  const rotcServicesData = {};
  const servicesData = {
    ...services,
  };

  // we split ROTC data from services
  ROTC_SERVICES.forEach((key) => {
    rotcServicesData[key] = services[key];
    delete servicesData[key];
  });

  /* istanbul ignore else */
  if (hasData(organizations)) {
    panes.push({
      menuItem: getMenuItem(
        intl,
        panes.length === activeTab,
        panes.length,
        messages.organizations,
        setActiveTab,
      ),
      render: /* istanbul ignore next */ () => (
        <Organizations organizations={organizations} />
      ),
    });
  }
  /**
   * @TODO
   * Validate has data for athletics
   */
  panes.push({
    menuItem: getMenuItem(
      intl,
      panes.length === activeTab,
      panes.length,
      messages.athletics,
      setActiveTab,
    ),
    render: /* istanbul ignore next */ () => <Athletics athletics={athletics} />,
  });

  if (hasData(greekLife)) {
    panes.push({
      menuItem: getMenuItem(
        intl,
        panes.length === activeTab,
        panes.length,
        messages.greekLife,
        setActiveTab,
      ),
      render: /* istanbul ignore next */ () => <GreekLife greekLife={greekLife} />,
    });
  }

  if (hasData(rotcServicesData)) {
    panes.push({
      menuItem: getMenuItem(
        intl,
        panes.length === activeTab,
        panes.length,
        messages.rotc,
        setActiveTab,
      ),
      render: /* istanbul ignore next */ () => <ROTC services={rotcServicesData} />,
    });
  }
  /* istanbul ignore else */
  if (hasData(servicesData)) {
    panes.push({
      menuItem: getMenuItem(
        intl,
        panes.length === activeTab,
        panes.length,
        messages.services,
        setActiveTab,
      ),
      render: /* istanbul ignore next */ () => <Services services={servicesData} />,
    });
  }
  /* istanbul ignore else */
  if (hasData(computingResources)) {
    panes.push({
      menuItem: getMenuItem(
        intl,
        panes.length === activeTab,
        panes.length,
        messages.computingResources,
        setActiveTab,
      ),
      render: /* istanbul ignore next */ () => (
        <ComputingResources computingResources={computingResources} />
      ),
    });
  }
  return panes;
}

function handleTabChange(hostContext, panes, activeIndex) {
  const tabClickId = panes[activeIndex].menuItem.key;
  const label = `studentlife-${tabClickId}`;
  const location = `${trackingLocations.TABS.STUDENT_LIFE}_${
    trackingLocations.TABS.TABS
  }_${camelcase(tabClickId)}`;
  trackView(hostContext, {
    type: trackClickTypes.HUBS_VIEW,
    location,
  });
  trackEventGa(hostContext, {
    type: 'click',
    category: 'Hubs Tabs',
    label,
  });
}

function OrganizationsAndServices(props) {
  const { intl, lastUpdatedYear, hostContext } = props;
  const [activeTab, setActiveTab] = useState(0);
  const panes = getPanes(props, activeTab, setActiveTab);
  return (
    <div
      className="organizations-services-container common-full-width"
      data-cy="organizations-services-container"
    >
      <div className="common-core-width">
        <h2 className="heading" tabIndex={0}>
          {intl.formatMessage(messages.title)}
        </h2>
        <div className="tabs-container">
          <Tab
            menu={{
              secondary: true,
              pointing: true,
              role: 'tablist',
            }}
            panes={panes}
            onTabChange={(e, { activeIndex }) => {
              handleTabChange(hostContext, panes, activeIndex);
              setActiveTab(activeIndex);
            }}
            activeIndex={activeTab}
          />
        </div>
      </div>
      {lastUpdatedYear ? (
        <div className="organizations-services-footer" tabIndex={0}>
          {`Latest available data from ${lastUpdatedYear}`}
        </div>
      ) : null}
    </div>
  );
}

OrganizationsAndServices.propTypes = {
  props: PropTypes.object,
};

export default injectIntl(withHostContext(OrganizationsAndServices));
